import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingCart,
  Wrench,
  Settings,
  AlertTriangle,
  Zap,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Phone,
  Building2,
  MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatCOP, WHATSAPP_LINK, buildWhatsAppMessage } from "@/lib/constants";

const SERVICES = [
  { id: "venta", label: "Venta", icon: ShoppingCart, desc: "Transformador nuevo" },
  { id: "reparacion", label: "Reparación", icon: Wrench, desc: "Reparar equipo existente" },
  { id: "mantenimiento", label: "Mantenimiento", icon: Settings, desc: "Preventivo o correctivo" },
];

const URGENCIES = [
  { id: "estandar", label: "Estándar", desc: "5-10 días hábiles", mult: "1.0x" },
  { id: "prioritario", label: "Prioritario", desc: "2-4 días hábiles", mult: "1.15x" },
  { id: "emergencia", label: "Emergencia", desc: "< 24 horas", mult: "1.35x" },
];

export default function Quoter() {
  const [step, setStep] = useState(0);
  const [service, setService] = useState("reparacion");
  const [kva, setKva] = useState(112);
  const [urgency, setUrgency] = useState("estandar");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    company: "",
    email: "",
    notes: "",
  });
  const [result, setResult] = useState(null);

  // local instant estimate (mirror of backend logic for UX)
  const localEstimate = useMemo(() => {
    const base = { venta: 180000, reparacion: 55000, mantenimiento: 22000 }[service];
    const um = { estandar: 1.0, prioritario: 1.15, emergencia: 1.35 }[urgency];
    const fixed = service === "mantenimiento" ? 180000 : 350000;
    const raw = base * Math.max(kva, 5) * um + fixed;
    const lo = Math.round((raw * 0.85) / 10000) * 10000;
    const hi = Math.round((raw * 1.2) / 10000) * 10000;
    return { lo, hi };
  }, [service, kva, urgency]);

  const next = () => setStep((s) => Math.min(s + 1, 3));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Nombre y teléfono son obligatorios");
      return;
    }
    setResult({
      service_type: service,
      transformer_type: "aceite",
      kva,
      urgency,
      ...form,
      estimated_min: localEstimate.lo,
      estimated_max: localEstimate.hi,
    });
    setStep(3);
    toast.success("Cotización generada correctamente");
  };

  const reset = () => {
    setStep(0);
    setService("reparacion");
    setKva(112);
    setUrgency("estandar");
    setForm({ name: "", phone: "", company: "", email: "", notes: "" });
    setResult(null);
  };

  const waMsg = result
    ? buildWhatsAppMessage(
        `Hola, soy ${result.name}. Generé una cotización en su web para ${result.service_type} de transformador ${result.transformer_type} de ${result.kva} kVA (urgencia: ${result.urgency}). Estimado: ${formatCOP(result.estimated_min)} - ${formatCOP(result.estimated_max)}. Quisiera continuar.`
      )
    : WHATSAPP_LINK;

  return (
    <section
      id="cotizador"
      data-testid="quoter-section"
      className="relative py-24 lg:py-32 bg-[#0a0a0a] overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
      <div className="absolute top-1/2 -translate-y-1/2 -left-32 w-96 h-96 bg-[#ff6b00]/20 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left intro */}
          <div className="lg:col-span-4 lg:sticky lg:top-32">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#ff6b00] mb-4 flex items-center gap-3">
              <span className="w-8 h-px bg-[#ff6b00]" />
              <span>Cotizador en tiempo real</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mb-6">
              Calcula tu
              <br />
              <span className="text-[#ff6b00]">cotización</span>
              <br />
              en 60 seg.
            </h2>
            <p className="text-[#a0aabf] leading-relaxed mb-8">
              Indícanos el servicio y la capacidad de tu transformador y
              obtén un estimado inmediato. Sin compromisos, sin spam.
            </p>
            <div className="space-y-3 text-sm">
              {[
                "Cálculo basado en mercado colombiano",
                "Estimado al instante",
                "Cotización final tras inspección",
              ].map((x) => (
                <div key={x} className="flex items-center gap-3 text-[#a0aabf]">
                  <CheckCircle2 className="w-4 h-4 text-[#ff6b00]" />
                  <span>{x}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quoter widget */}
          <div className="lg:col-span-8">
            <div
              data-testid="quoter-widget"
              className="glass relative p-6 sm:p-10"
            >
              <div className="absolute -top-px left-10 right-10 h-px electric-line" />

              {/* Step indicator */}
              <div className="flex items-center justify-between mb-8">
                <div className="font-mono text-xs text-[#a0aabf]">
                  PASO {Math.min(step + 1, 4)} / 04
                </div>
                <div className="flex gap-1.5">
                  {[0, 1, 2, 3].map((i) => (
                    <span
                      key={i}
                      className={`h-1 transition-all ${
                        i <= step ? "bg-[#ff6b00] w-8" : "bg-white/10 w-4"
                      }`}
                    />
                  ))}
                </div>
              </div>

              <AnimatePresence mode="wait">
                {step === 0 && (
                  <motion.div
                    key="s0"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                  >
                    <h3 className="font-display text-2xl font-bold mb-2">
                      ¿Qué servicio necesitas?
                    </h3>
                    <p className="text-sm text-[#a0aabf] mb-8">
                      Selecciona la opción que mejor describa tu necesidad.
                    </p>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {SERVICES.map((s) => (
                        <button
                          key={s.id}
                          data-testid={`quote-service-${s.id}`}
                          onClick={() => setService(s.id)}
                          className={`text-left p-5 border transition-all ${
                            service === s.id
                              ? "border-[#ff6b00] bg-[#ff6b00]/10"
                              : "border-white/10 bg-white/5 hover:border-white/30"
                          }`}
                        >
                          <s.icon
                            className={`w-7 h-7 mb-4 ${
                              service === s.id ? "text-[#ff6b00]" : "text-white"
                            }`}
                            strokeWidth={1.5}
                          />
                          <div className="font-display font-bold text-lg">
                            {s.label}
                          </div>
                          <div className="text-xs text-[#a0aabf] mt-1">
                            {s.desc}
                          </div>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {step === 1 && (
                  <motion.div
                    key="s1"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                  >
                    <h3 className="font-display text-2xl font-bold mb-2">
                      Capacidad
                    </h3>
                    <p className="text-sm text-[#a0aabf] mb-8">
                      Indica la potencia en kVA. Las más comunes: 15, 30, 45, 75,
                      112.5, 150, 225, 300, 500.
                    </p>
                    <div className="bg-white/5 border border-white/10 p-6">
                      <div className="flex items-baseline justify-between mb-6">
                        <div className="text-xs uppercase tracking-[0.3em] text-[#a0aabf]">
                          Capacidad
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span
                            data-testid="quote-kva-value"
                            className="font-display text-5xl font-bold text-[#ff6b00] tabular-nums"
                          >
                            {kva}
                          </span>
                          <span className="text-sm text-[#a0aabf] font-mono">
                            kVA
                          </span>
                        </div>
                      </div>
                      <Slider
                        data-testid="quote-kva-slider"
                        value={[kva]}
                        min={5}
                        max={1000}
                        step={5}
                        onValueChange={(v) => setKva(v[0])}
                        className="my-6"
                      />
                      <div className="flex flex-wrap gap-2 mt-6">
                        {[15, 30, 45, 75, 112, 150, 225, 300, 500, 750, 1000].map(
                          (v) => (
                            <button
                              key={v}
                              data-testid={`quote-kva-preset-${v}`}
                              onClick={() => setKva(v)}
                              className={`px-3 py-1.5 text-xs font-mono border transition-colors ${
                                kva === v
                                  ? "border-[#ff6b00] bg-[#ff6b00] text-black"
                                  : "border-white/10 text-[#a0aabf] hover:border-white/30"
                              }`}
                            >
                              {v}
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div
                    key="s2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                  >
                    <h3 className="font-display text-2xl font-bold mb-2">
                      Urgencia y datos de contacto
                    </h3>
                    <p className="text-sm text-[#a0aabf] mb-6">
                      Selecciona urgencia y déjanos tus datos. Confirmamos en
                      menos de 1 hora hábil.
                    </p>

                    <div className="grid sm:grid-cols-3 gap-3 mb-6">
                      {URGENCIES.map((u) => (
                        <button
                          key={u.id}
                          data-testid={`quote-urgency-${u.id}`}
                          onClick={() => setUrgency(u.id)}
                          className={`text-left p-4 border transition-all ${
                            urgency === u.id
                              ? "border-[#ff6b00] bg-[#ff6b00]/10"
                              : "border-white/10 bg-white/5 hover:border-white/30"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-display font-bold">
                              {u.label}
                            </span>
                            <span className="font-mono text-[10px] text-[#ff6b00]">
                              {u.mult}
                            </span>
                          </div>
                          <div className="text-xs text-[#a0aabf]">{u.desc}</div>
                        </button>
                      ))}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-[#a0aabf]">
                          Nombre *
                        </Label>
                        <Input
                          data-testid="quote-input-name"
                          value={form.name}
                          onChange={(e) =>
                            setForm({ ...form, name: e.target.value })
                          }
                          className="bg-white/5 border-white/10 rounded-none h-12 mt-2 text-white"
                          placeholder="Tu nombre"
                        />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-[#a0aabf]">
                          Teléfono / WhatsApp *
                        </Label>
                        <Input
                          data-testid="quote-input-phone"
                          value={form.phone}
                          onChange={(e) =>
                            setForm({ ...form, phone: e.target.value })
                          }
                          className="bg-white/5 border-white/10 rounded-none h-12 mt-2 text-white"
                          placeholder="+57 ..."
                        />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-[#a0aabf]">
                          Empresa
                        </Label>
                        <Input
                          data-testid="quote-input-company"
                          value={form.company}
                          onChange={(e) =>
                            setForm({ ...form, company: e.target.value })
                          }
                          className="bg-white/5 border-white/10 rounded-none h-12 mt-2 text-white"
                          placeholder="Opcional"
                        />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-[#a0aabf]">
                          Correo
                        </Label>
                        <Input
                          data-testid="quote-input-email"
                          value={form.email}
                          onChange={(e) =>
                            setForm({ ...form, email: e.target.value })
                          }
                          className="bg-white/5 border-white/10 rounded-none h-12 mt-2 text-white"
                          placeholder="Opcional"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs uppercase tracking-wider text-[#a0aabf]">
                          Notas
                        </Label>
                        <Textarea
                          data-testid="quote-input-notes"
                          value={form.notes}
                          onChange={(e) =>
                            setForm({ ...form, notes: e.target.value })
                          }
                          className="bg-white/5 border-white/10 rounded-none mt-2 text-white"
                          placeholder="Cuéntanos más sobre tu transformador o falla..."
                          rows={3}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 3 && result && (
                  <motion.div
                    key="s3"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                    data-testid="quote-result"
                  >
                    <div className="text-center mb-8">
                      <div className="inline-flex items-center justify-center w-14 h-14 bg-[#ff6b00]/10 border border-[#ff6b00]/40 mb-5">
                        <Zap className="w-7 h-7 text-[#ff6b00]" />
                      </div>
                      <h3 className="font-display text-3xl font-bold mb-2">
                        Tu cotización estimada
                      </h3>
                      <p className="text-sm text-[#a0aabf]">
                        Valor de referencia · Confirmamos por WhatsApp en menos
                        de 1 hora hábil.
                      </p>
                    </div>

                    <div className="bg-white/5 border border-[#ff6b00]/40 p-8 text-center mb-6">
                      <div className="text-xs uppercase tracking-[0.3em] text-[#a0aabf] mb-3">
                        Rango estimado · COP
                      </div>
                      <div className="font-display text-3xl sm:text-5xl font-bold text-[#ff6b00] tracking-tight">
                        {formatCOP(result.estimated_min)}
                      </div>
                      <div className="my-2 text-[#a0aabf] text-sm">a</div>
                      <div className="font-display text-3xl sm:text-5xl font-bold text-[#ff6b00] tracking-tight">
                        {formatCOP(result.estimated_max)}
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3 mb-6 font-mono text-xs">
                      {[
                        ["Servicio", result.service_type],
                        ["Capacidad", `${result.kva} kVA`],
                        ["Urgencia", result.urgency],
                      ].map(([k, v]) => (
                        <div
                          key={k}
                          className="flex items-center justify-between border border-white/5 p-3 bg-white/5"
                        >
                          <span className="text-[#a0aabf] uppercase tracking-wider">
                            {k}
                          </span>
                          <span className="text-white capitalize">{v}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href={waMsg}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="quote-result-whatsapp"
                        className="flex-1"
                      >
                        <Button className="w-full bg-[#25D366] hover:bg-[#1eb854] text-white font-bold uppercase tracking-wider text-sm rounded-none h-14">
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Continuar por WhatsApp
                        </Button>
                      </a>
                      <Button
                        data-testid="quote-result-reset"
                        onClick={reset}
                        variant="outline"
                        className="border-white/20 hover:border-white text-white hover:bg-white/5 bg-transparent rounded-none h-14 uppercase tracking-wider text-sm"
                      >
                        Nueva cotización
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Live estimate strip */}
              {step < 3 && (
                <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0aabf] mb-1">
                      Estimado en vivo
                    </div>
                    <div className="font-display text-lg font-bold text-white tabular-nums">
                      {formatCOP(localEstimate.lo)}{" "}
                      <span className="text-[#a0aabf] text-sm font-normal">—</span>{" "}
                      {formatCOP(localEstimate.hi)}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {step > 0 && (
                      <Button
                        data-testid="quote-btn-back"
                        onClick={back}
                        variant="outline"
                        className="border-white/10 hover:border-white text-white hover:bg-white/5 bg-transparent rounded-none uppercase tracking-wider text-xs h-12"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Atrás
                      </Button>
                    )}
                    {step < 2 && (
                      <Button
                        data-testid="quote-btn-next"
                        onClick={next}
                        className="bg-[#ff6b00] hover:bg-[#e65a00] text-black font-bold uppercase tracking-wider text-xs rounded-none h-12"
                      >
                        Siguiente
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                    {step === 2 && (
                      <Button
                        data-testid="quote-btn-submit"
                        onClick={submit}
                        className="bg-[#ff6b00] hover:bg-[#e65a00] text-black font-bold uppercase tracking-wider text-xs rounded-none h-12"
                      >
                        Generar cotización
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
